import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import api from '../../services/api';

const SetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [userData, setUserData] = useState<{ name: string; email: string } | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('Token não fornecido.');
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/public/password-reset/validate/${token}`);
      if (response.data.valid) {
        setValidToken(true);
        setUserData(response.data.user);
      } else {
        setError('Token inválido ou expirado.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao validar token.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.post('/public/password-reset/confirm', {
        token,
        newPassword: password
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao definir senha.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Validando token...</Typography>
      </Container>
    );
  }

  if (!validToken) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom color="error">
            Token Inválido
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || 'O link de definição de senha é inválido ou expirou.'}
          </Alert>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button variant="contained" onClick={() => navigate('/')}>
              Voltar ao Início
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom color="success.main">
            Senha Definida!
          </Typography>
          <Alert severity="success" sx={{ mt: 2 }}>
            Sua senha foi definida com sucesso! Você será redirecionado para a página de login.
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Definir Senha
        </Typography>
        
        {userData && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Nome:</strong> {userData.name}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {userData.email}
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            type="password"
            label="Nova Senha"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            helperText="Mínimo 6 caracteres"
          />
          
          <TextField
            type="password"
            label="Confirmar Senha"
            fullWidth
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            error={confirmPassword !== '' && password !== confirmPassword}
            helperText={confirmPassword !== '' && password !== confirmPassword ? 'Senhas não coincidem' : ''}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Definir Senha'}
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default SetPassword;
